import { useEffect } from 'react';
import type React from 'react';
import type { Content } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { plainTextToTiptapDoc } from '@/lib/plain-text-tiptap';

interface ArtifactBodyRendererProps {
  /** Tiptap JSON document. Falls back to `plainText` if not a valid doc. */
  json: unknown;
  /** Optional plain-text fallback used when `json` is not a valid Tiptap doc. */
  plainText?: string;
  /** Extra classes for the editor surface. */
  className?: string;
  /** Aria label for the editor surface. */
  ariaLabel?: string;
}

/**
 * Read-only Tiptap renderer for an artifact body. Used by the collaborative
 * editor (read-only fallback) and the AI draft preview mode.
 *
 * Intentionally minimal: no collab, no cursor presence, no toolbar — just
 * render the supplied Tiptap JSON.
 */
export function ArtifactBodyRenderer({
  json,
  plainText,
  className,
  ariaLabel,
}: ArtifactBodyRendererProps): React.ReactElement {
  const editor = useEditor({
    extensions: [StarterKit.configure({ history: false })],
    editable: false,
    content: resolveContent(json, plainText),
  });

  useEffect(() => {
    if (editor === null) return;
    try {
      editor.commands.setContent(resolveContent(json, plainText), false);
    } catch {
      editor.commands.setContent(
        plainTextToTiptapDoc(plainText ?? '') as unknown as Content,
        false
      );
    }
  }, [editor, json, plainText]);

  return (
    <EditorContent
      editor={editor}
      className={
        className ??
        'artifact-prose min-h-[16rem] max-w-none rounded-md border border-border bg-surface p-4 focus:outline-none'
      }
      aria-label={ariaLabel ?? 'Artifact body preview'}
    />
  );
}

function resolveContent(json: unknown, plainText: string | undefined): Content {
  if (
    json !== null &&
    typeof json === 'object' &&
    !Array.isArray(json) &&
    (json as { type?: unknown }).type === 'doc'
  ) {
    return json as Content;
  }
  return plainTextToTiptapDoc(plainText ?? '') as unknown as Content;
}
